import { useEffect, useRef, useState } from 'react';

import { Alert, Button, Icon, TextField, Typography } from '@/ui';

import { MentoCalPageShell } from './PageShell';
import { SCHEDULING_CANCELLATION_POLICY_URL } from './constants';
import { CoachHeader, DetailRow, SessionCardShell } from './sessionCard';
import {
  formatCancellationSessionRange,
  isSessionStarted,
  isWithinRescheduleWindow,
} from './sessionTiming';

import type {
  AppointmentScope,
  DecodedBrowserAppointment,
  ParticipantAppointment,
  ParticipantAppointmentDetail,
} from '@/features/appointments/types';
import type { Session } from '@/types';
import type { FC, ReactNode } from 'react';

import { appointmentRejectionMessage } from '@/features/appointments/appointmentStatusCopy';
import { PREVIOUS_EXACT_APPOINTMENT_CONTRACT } from '@/features/appointments/browserCapability';
import { shouldReconcileParticipantAppointmentWrite } from '@/features/appointments/participantAppointmentError';
import {
  appointmentSeriesCommandFields,
  appointmentUpgradeGuardSpent,
  canonicalAppointmentCommand,
  storedAppointmentLifecycleTransport,
  useAppointmentLifecycleGuard,
} from '@/features/appointments/useAppointmentLifecycleGuard';
import {
  getAppointmentBrowserCapability,
  shouldReconcileAppointmentWrite,
  useCancelAppointmentMutation,
  useGetAppointmentOperationQuery,
  useGetAppointmentQuery,
  useLazyGetAppointmentQuery,
} from '@/store/api/appointmentApi';
import {
  useCancelMomentsParticipantAppointmentAsMemberMutation,
  useGetMomentsParticipantAppointmentOperationQuery,
  useGetMomentsParticipantAppointmentQuery,
  useLazyGetMomentsParticipantAppointmentQuery,
  useParticipantAppointmentRouteDecision,
} from '@/store/api/momentsParticipantAppointmentApi';
import {
  useCancelSessionMutation,
  useLazyGetSessionAfterCancellationFailureQuery,
} from '@/store/api/sessionApi';

interface MemberCancelSessionProps {
  session: Session;
  close: () => void;
  onCancelled?: () => void;
  onRescheduleInstead?: () => void;
  participantAppointmentTransport?: 'moments' | 'rails';
  userId?: string;
  within48h?: boolean;
}

interface RetainedCancellationCommand {
  appointmentId: string;
  contractVersion?: 'appointment-cancel.v1';
  expectedSeriesMutationVersion: number | null;
  expectedVersion: number;
  reason: string | null;
  scope: AppointmentScope;
  seriesId: string | null;
}

const RETAINED_CANCELLATION_KEYS = [
  'appointmentId',
  'expectedSeriesMutationVersion',
  'expectedVersion',
  'reason',
  'scope',
  'seriesId',
] as const;

const retainedCancellationCommand = (
  bytes: string,
  appointmentId: string,
): RetainedCancellationCommand | undefined => {
  try {
    const value = JSON.parse(bytes) as unknown;
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return undefined;
    }
    const command = value as Record<string, unknown>;
    const validStandalone =
      command.seriesId === null &&
      command.expectedSeriesMutationVersion === null;
    const validSeries =
      typeof command.seriesId === 'string' &&
      command.seriesId.length > 0 &&
      Number.isInteger(command.expectedSeriesMutationVersion) &&
      (command.expectedSeriesMutationVersion as number) > 0;
    const commandKeys = Object.keys(command);
    const validKeySet =
      commandKeys.length === RETAINED_CANCELLATION_KEYS.length ||
      (commandKeys.length === RETAINED_CANCELLATION_KEYS.length + 1 &&
        'contractVersion' in command);
    if (
      !validKeySet ||
      !RETAINED_CANCELLATION_KEYS.every((key) => key in command) ||
      command.appointmentId !== appointmentId ||
      (command.contractVersion !== undefined &&
        command.contractVersion !== 'appointment-cancel.v1') ||
      !Number.isInteger(command.expectedVersion) ||
      (command.expectedVersion as number) <= 0 ||
      (command.reason !== null && typeof command.reason !== 'string') ||
      (command.scope !== 'appointment' &&
        command.scope !== 'this_and_following') ||
      (!validStandalone && !validSeries)
    ) {
      return undefined;
    }
    return command as unknown as RetainedCancellationCommand;
  } catch {
    return undefined;
  }
};

export const MemberCancelSession: FC<MemberCancelSessionProps> = ({
  close,
  onCancelled,
  onRescheduleInstead,
  participantAppointmentTransport,
  session,
  userId,
  within48h,
}) => {
  const [step, setStep] = useState<'speedbump' | 'form'>('speedbump');
  const [reason, setReason] = useState('');
  const [pendingOperationId, setPendingOperationId] = useState<string | null>(
    null,
  );
  const [
    pendingOperationRetryAfterSeconds,
    setPendingOperationRetryAfterSeconds,
  ] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [operationReviewRequired, setOperationReviewRequired] = useState(false);
  const submitInFlightRef = useRef(false);
  const reasonOwnerRef = useRef<string | null>(null);
  const restoredPendingAppliedRef = useRef(false);
  const [cancelSession, { isLoading: isSubmitting }] =
    useCancelSessionMutation();
  const [refreshSessionAfterFailure] =
    useLazyGetSessionAfterCancellationFailureQuery();
  const [cancelAppointment, { isLoading: isNativeSubmitting }] =
    useCancelAppointmentMutation();
  const [cancelParticipantAppointment, participantCancellationMutation] =
    useCancelMomentsParticipantAppointmentAsMemberMutation();
  const appointmentCapability = getAppointmentBrowserCapability();
  const [refreshAppointment] = useLazyGetAppointmentQuery();
  const [refreshParticipantAppointment] =
    useLazyGetMomentsParticipantAppointmentQuery();
  const nativeAppointmentId = session.sourceAppointmentId;
  const nativeAppointmentVersion = session.sourceAppointmentVersion;
  const nativeOwnerKey = nativeAppointmentId
    ? `member-cancel:${session.id}:${nativeAppointmentId}`
    : undefined;
  const persistedTransport = storedAppointmentLifecycleTransport(
    nativeOwnerKey,
    'rails',
  );
  const participantAppointmentBaseline =
    useGetMomentsParticipantAppointmentQuery(
      { appointmentId: nativeAppointmentId ?? '' },
      {
        refetchOnMountOrArgChange: true,
        skip:
          !nativeAppointmentId ||
          persistedTransport === 'rails' ||
          participantAppointmentTransport === 'rails',
      },
    );
  const [establishedRoute, setEstablishedRoute] = useState<{
    appointmentId: string;
    transport: 'moments';
  }>();
  const establishedTransport =
    establishedRoute && establishedRoute.appointmentId === nativeAppointmentId
      ? establishedRoute.transport
      : undefined;
  const routeDecision = useParticipantAppointmentRouteDecision({
    coachExternalId:
      nativeAppointmentId &&
      !persistedTransport &&
      !participantAppointmentTransport
        ? (
            participantAppointmentBaseline.currentData ??
            participantAppointmentBaseline.data
          )?.coachSelector.externalId
        : undefined,
    establishedTransport:
      persistedTransport ??
      participantAppointmentTransport ??
      establishedTransport,
    flow: 'cancellation',
  });
  const nativeTransport =
    persistedTransport ??
    participantAppointmentTransport ??
    establishedTransport ??
    (routeDecision.state === 'ready' ? routeDecision.transport : undefined);
  const useDirectParticipantTransport = nativeTransport === 'moments';
  useEffect(() => {
    if (
      nativeAppointmentId &&
      !participantAppointmentTransport &&
      routeDecision.state === 'ready' &&
      routeDecision.transport === 'moments'
    ) {
      setEstablishedRoute({
        appointmentId: nativeAppointmentId,
        transport: 'moments',
      });
    }
  }, [nativeAppointmentId, participantAppointmentTransport, routeDecision]);
  const railsAppointmentBaseline = useGetAppointmentQuery(
    { appointmentId: nativeAppointmentId ?? '' },
    {
      refetchOnMountOrArgChange: true,
      skip: !nativeAppointmentId || nativeTransport !== 'rails',
    },
  );
  const {
    data: nativeAppointmentBaseline,
    isError: isNativeBaselineError,
    isFetching: isNativeBaselineFetching,
    isLoading: isNativeBaselineLoading,
  } = useDirectParticipantTransport
    ? participantAppointmentBaseline
    : railsAppointmentBaseline;
  const [
    reviewedNativeAppointmentBaseline,
    setReviewedNativeAppointmentBaseline,
  ] = useState<
    | DecodedBrowserAppointment
    | ParticipantAppointment
    | ParticipantAppointmentDetail
    | null
  >(null);
  useEffect(() => {
    if (
      isNativeBaselineFetching ||
      isNativeBaselineError ||
      !nativeAppointmentId ||
      nativeAppointmentBaseline?.id !== nativeAppointmentId
    ) {
      return;
    }
    setReviewedNativeAppointmentBaseline((current) =>
      current?.id === nativeAppointmentId ? current : nativeAppointmentBaseline,
    );
  }, [
    isNativeBaselineFetching,
    isNativeBaselineError,
    nativeAppointmentBaseline,
    nativeAppointmentId,
  ]);
  const authoritativeNativeAppointmentBaseline =
    nativeAppointmentId &&
    reviewedNativeAppointmentBaseline?.id === nativeAppointmentId
      ? reviewedNativeAppointmentBaseline
      : null;
  const displayedSession = authoritativeNativeAppointmentBaseline
    ? ({
        ...session,
        endedAt: authoritativeNativeAppointmentBaseline.currentSchedule.endAt,
        startedAt:
          authoritativeNativeAppointmentBaseline.currentSchedule.startAt,
      } as Session)
    : session;
  const isWithin = nativeAppointmentId
    ? isWithinRescheduleWindow(displayedSession.startedAt)
    : (within48h ?? isWithinRescheduleWindow(session.startedAt));
  // MEN-4241: as in MemberRescheduleSession, a native appointment waits for its
  // authoritative baseline before any past-session decision. This stays
  // undefined until the read lands, so a stale `session.startedAt` carried in
  // from the list row cannot strand a still-upcoming appointment.
  const authoritativeStartsAt = nativeAppointmentId
    ? authoritativeNativeAppointmentBaseline?.currentSchedule.startAt
    : session.startedAt;
  const [sessionPassed, setSessionPassed] = useState(false);
  const nativeCancellationUnavailable = Boolean(
    authoritativeNativeAppointmentBaseline &&
    (authoritativeNativeAppointmentBaseline.lifecycleStatus === 'cancelled' ||
      authoritativeNativeAppointmentBaseline.lifecycleStatus ===
        'cancellation_pending' ||
      !authoritativeNativeAppointmentBaseline.actionCapabilities.cancel
        .allowed ||
      !authoritativeNativeAppointmentBaseline.actionCapabilities.cancel.scopes.includes(
        'appointment',
      )),
  );
  const nativeRouteBlocked = Boolean(
    nativeAppointmentId &&
    !participantAppointmentTransport &&
    !establishedTransport &&
    routeDecision.state !== 'ready',
  );
  const nativeRouteUnavailable =
    nativeRouteBlocked && routeDecision.state === 'unavailable';
  const isNativeBaselinePending = Boolean(
    nativeAppointmentId &&
    !authoritativeNativeAppointmentBaseline &&
    (nativeRouteBlocked ||
      isNativeBaselineLoading ||
      isNativeBaselineFetching ||
      isNativeBaselineError ||
      nativeAppointmentBaseline?.id === nativeAppointmentId),
  );
  const displayedScope: AppointmentScope = 'appointment';
  const [baselineDriftDetected, setBaselineDriftDetected] = useState(false);
  useEffect(() => setBaselineDriftDetected(false), [nativeOwnerKey]);
  const reasonOwnerKey = nativeOwnerKey ?? `legacy-cancel:${session.id}`;
  const currentOwnerKeyRef = useRef(nativeOwnerKey);
  currentOwnerKeyRef.current = nativeOwnerKey;
  const lifecycle = useAppointmentLifecycleGuard({
    allowReviewedPreimage: false,
    capability: appointmentCapability,
    displayed: authoritativeNativeAppointmentBaseline
      ? {
          id: authoritativeNativeAppointmentBaseline.id,
          seriesId: authoritativeNativeAppointmentBaseline.seriesId,
          seriesMutationVersion:
            authoritativeNativeAppointmentBaseline.seriesMutationVersion,
          version: authoritativeNativeAppointmentBaseline.version,
        }
      : {
          id: nativeAppointmentId,
          seriesId: session.sourceAppointmentSeriesId,
          seriesMutationVersion: session.sourceAppointmentSeriesMutationVersion,
          version: nativeAppointmentVersion,
        },
    legacyTransport: 'rails',
    ownerKey: nativeOwnerKey,
    protocolMarker: PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
    readAppointment: async () => {
      const request = useDirectParticipantTransport
        ? refreshParticipantAppointment({
            appointmentId: nativeAppointmentId ?? '',
          })
        : refreshAppointment({ appointmentId: nativeAppointmentId ?? '' });
      return request.unwrap().finally(() => request.unsubscribe());
    },
    transport: nativeTransport,
  });
  const {
    clearOwnedOperation,
    consumeConfirmedReceipt,
    currentCommand,
    recordConfirmed,
    rereadAfterRejection,
  } = lifecycle;
  const nativeCancellationBlocksSubmit =
    nativeCancellationUnavailable &&
    !(
      lifecycle.restoredSubmitting &&
      authoritativeNativeAppointmentBaseline?.lifecycleStatus ===
        'cancellation_pending'
    );
  const restoredSubmissionOwnerKey = (
    lifecycle.restoredSubmitting as { ownerKey?: string } | null
  )?.ownerKey;
  const [reconcilingOwnerKey, setReconcilingOwnerKey] = useState<string | null>(
    restoredSubmissionOwnerKey === nativeOwnerKey
      ? (nativeOwnerKey ?? null)
      : null,
  );
  const [restoredReviewUnavailable, setRestoredReviewUnavailable] =
    useState(false);
  const isReconcilingRestored = reconcilingOwnerKey === nativeOwnerKey;
  const canPollPendingOperation =
    Boolean(pendingOperationId) && !upgradeRequired && !operationReviewRequired;
  const isCheckingCancellationStatus =
    isReconcilingRestored || canPollPendingOperation;
  // MEN-4241: a past session gets the terminal card the reschedule flow already
  // shows instead of the cancellation speedbump -- but only when this
  // cancellation's outcome is still UNRESOLVED. MemberRescheduleSession ranks
  // 'pending' and 'confirmed' above 'past' for the same reason: the form owns
  // the pending, retained and upgrade messages, and `requestClose` refuses to
  // close while a status check is in flight, which would leave this card's
  // Dismiss inert. `sessionPassed` latches the submit guard so a session that
  // starts while the form is open re-renders here instead of swallowing a click.
  //
  // `lifecycle.rejected` is deliberately absent: a rejection is RESOLVED -- the
  // server answered, the appointment is still scheduled, and the form says so.
  // Counting it as outstanding kept `isPast` false forever after a rejection, so
  // crossing the start boundary left an enabled retry that no-ops against the
  // submit guard rather than showing the terminal card. PR #1650 review.
  // `cancellation_pending` on the baseline is the server reporting a
  // cancellation still in flight. Every other unresolved signal here is local
  // state, so on a fresh mount -- the member cancelled elsewhere, then opened
  // the calendar link here -- this is the only thing that sees it. `cancelled`
  // is deliberately excluded for the same reason as `rejected`: it is resolved.
  const nativeCancellationInFlight =
    authoritativeNativeAppointmentBaseline?.lifecycleStatus ===
    'cancellation_pending';
  const cancellationOutcomeUnresolved = Boolean(
    isCheckingCancellationStatus ||
    pendingOperationId ||
    operationReviewRequired ||
    upgradeRequired ||
    lifecycle.restoredPending ||
    lifecycle.restoredSubmitting ||
    nativeCancellationInFlight,
  );
  const isPast =
    !cancellationOutcomeUnresolved &&
    (sessionPassed ||
      Boolean(
        authoritativeStartsAt && isSessionStarted(authoritativeStartsAt),
      ));
  const restoredCancellationCommand =
    lifecycle.restoredSubmitting && nativeAppointmentId
      ? retainedCancellationCommand(
          lifecycle.restoredSubmitting.bytes,
          nativeAppointmentId,
        )
      : undefined;
  const invalidRestoredSubmittingCommand = Boolean(
    lifecycle.restoredSubmitting &&
    nativeAppointmentId &&
    !restoredCancellationCommand,
  );
  const restoredCancellationReasonChanged = Boolean(
    restoredCancellationCommand &&
    restoredCancellationCommand.reason !== (reason.trim() || null),
  );
  const restoredCancellationScopeChanged = Boolean(
    restoredCancellationCommand &&
    restoredCancellationCommand.scope !== displayedScope,
  );
  const rejectedCancellationCommand =
    lifecycle.rejected && lifecycle.reviewed && nativeAppointmentId
      ? canonicalAppointmentCommand({
          appointmentId: nativeAppointmentId,
          expectedVersion: lifecycle.reviewed.version,
          reason: reason.trim() || null,
          scope: displayedScope,
          ...appointmentSeriesCommandFields(
            appointmentCapability,
            lifecycle.reviewed,
          ),
        })
      : null;
  const rejectedCancellationCommandUnchanged = Boolean(
    rejectedCancellationCommand &&
    rejectedCancellationCommand === lifecycle.rejected?.bytes,
  );
  const hasRecoverableSubmittingCommand = Boolean(
    restoredCancellationCommand &&
    !restoredCancellationReasonChanged &&
    !restoredCancellationScopeChanged &&
    !submitInFlightRef.current,
  );
  const requiresLatestSessionDetails =
    lifecycle.reviewUnavailable ||
    upgradeRequired ||
    isNativeBaselineError ||
    nativeRouteBlocked ||
    baselineDriftDetected ||
    nativeCancellationBlocksSubmit ||
    operationReviewRequired ||
    restoredReviewUnavailable ||
    invalidRestoredSubmittingCommand ||
    restoredCancellationReasonChanged ||
    restoredCancellationScopeChanged;
  const isProcessingCancellation =
    submitInFlightRef.current || isCheckingCancellationStatus;
  let cancellationStatusMessage: string | null = null;
  if (isProcessingCancellation) {
    cancellationStatusMessage =
      "We're confirming your cancellation. This may take a few seconds.";
  } else if (requiresLatestSessionDetails) {
    cancellationStatusMessage =
      "We're checking the latest session details. Refresh the page to continue.";
  } else if (
    lifecycle.rejected ||
    submitError ||
    hasRecoverableSubmittingCommand
  ) {
    cancellationStatusMessage =
      'This session is still scheduled. You can try cancelling again.';
  }
  let cancellationButtonLabel = 'Cancel session';
  if (lifecycle.restoredSubmitting && !requiresLatestSessionDetails) {
    cancellationButtonLabel = 'Try cancellation again';
  }
  if (isProcessingCancellation) {
    cancellationButtonLabel = 'Checking cancellation status...';
  }
  const restoredCompletionRef = useRef({
    clearOwnedOperation,
    close,
    onCancelled,
  });
  restoredCompletionRef.current = {
    clearOwnedOperation,
    close,
    onCancelled,
  };
  const railsOperationStatus = useGetAppointmentOperationQuery(
    {
      browserContractMarker: PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
      operationId: pendingOperationId ?? '',
    },
    {
      pollingInterval: upgradeRequired
        ? 0
        : Math.max(1000, (pendingOperationRetryAfterSeconds ?? 5) * 1000),
      skip: !canPollPendingOperation || useDirectParticipantTransport,
    },
  );
  const participantOperationStatus =
    useGetMomentsParticipantAppointmentOperationQuery(
      { operationId: pendingOperationId ?? '' },
      {
        pollingInterval: upgradeRequired
          ? 0
          : Math.max(1000, (pendingOperationRetryAfterSeconds ?? 5) * 1000),
        skip: !canPollPendingOperation || !useDirectParticipantTransport,
      },
    );
  const operationStatus = useDirectParticipantTransport
    ? participantOperationStatus
    : railsOperationStatus;
  const requestClose = () => {
    if (
      isCheckingCancellationStatus ||
      lifecycle.isReading ||
      submitInFlightRef.current ||
      isSubmitting ||
      isNativeSubmitting
    )
      return;
    close();
  };

  useEffect(() => {
    if (reasonOwnerRef.current !== reasonOwnerKey) {
      reasonOwnerRef.current = reasonOwnerKey;
      setReason('');
    }
    if (lifecycle.restoredPending && !restoredPendingAppliedRef.current) {
      restoredPendingAppliedRef.current = true;
      setStep('form');
      setPendingOperationId(lifecycle.restoredPending.operationId);
      setPendingOperationRetryAfterSeconds(
        lifecycle.restoredPending.retryAfterSeconds,
      );
    } else if (lifecycle.rejected || lifecycle.restoredSubmitting) {
      setStep('form');
      if (
        lifecycle.restoredSubmitting &&
        nativeAppointmentId &&
        restoredSubmissionOwnerKey === nativeOwnerKey
      ) {
        const restoredCommand = retainedCancellationCommand(
          lifecycle.restoredSubmitting.bytes,
          nativeAppointmentId,
        );
        if (restoredCommand) setReason(restoredCommand.reason ?? '');
      }
    }
  }, [
    lifecycle.rejected,
    lifecycle.restoredPending,
    lifecycle.restoredSubmitting,
    nativeAppointmentId,
    nativeOwnerKey,
    reasonOwnerKey,
    restoredSubmissionOwnerKey,
  ]);

  useEffect(() => {
    if (!lifecycle.confirmedReceipt) return;
    if (!consumeConfirmedReceipt()) return;
    onCancelled?.();
    close();
  }, [close, consumeConfirmedReceipt, lifecycle.confirmedReceipt, onCancelled]);

  useEffect(() => {
    if (appointmentUpgradeGuardSpent(operationStatus.error)) {
      setUpgradeRequired(true);
    }
  }, [operationStatus.error]);

  useEffect(() => {
    if (
      !lifecycle.restoredSubmitting ||
      !nativeAppointmentId ||
      !nativeOwnerKey ||
      restoredSubmissionOwnerKey !== nativeOwnerKey ||
      submitInFlightRef.current
    ) {
      return undefined;
    }

    setReconcilingOwnerKey(nativeOwnerKey);
    setRestoredReviewUnavailable(false);
    const request = useDirectParticipantTransport
      ? refreshParticipantAppointment({ appointmentId: nativeAppointmentId })
      : refreshAppointment({ appointmentId: nativeAppointmentId });
    let active = true;
    let subscribed = true;
    const unsubscribe = () => {
      if (!subscribed) return;
      subscribed = false;
      request.unsubscribe();
    };
    request
      .unwrap()
      .then((appointment) => {
        if (
          !active ||
          currentOwnerKeyRef.current !== nativeOwnerKey ||
          appointment.lifecycleStatus !== 'cancelled'
        ) {
          return;
        }
        const completion = restoredCompletionRef.current;
        completion.clearOwnedOperation();
        completion.onCancelled?.();
        completion.close();
      })
      .catch(() => {
        if (active) setRestoredReviewUnavailable(true);
      })
      .finally(() => {
        unsubscribe();
        if (active) {
          setReconcilingOwnerKey((current) =>
            current === nativeOwnerKey ? null : current,
          );
        }
      });

    return () => {
      active = false;
      unsubscribe();
      setRestoredReviewUnavailable(false);
      setReconcilingOwnerKey((current) =>
        current === nativeOwnerKey ? null : current,
      );
    };
  }, [
    lifecycle.restoredSubmitting,
    nativeAppointmentId,
    nativeOwnerKey,
    refreshAppointment,
    refreshParticipantAppointment,
    restoredSubmissionOwnerKey,
    useDirectParticipantTransport,
  ]);

  useEffect(() => {
    const operation = operationStatus.currentData;
    if (!operation || operation.id !== pendingOperationId) return;
    if (operation.status === 'pending') {
      setPendingOperationRetryAfterSeconds(operation.retryAfterSeconds ?? null);
    }
    if (operation.status === 'confirmed') {
      if (!operation.appointmentIds.includes(nativeAppointmentId ?? '')) {
        setOperationReviewRequired(true);
        setPendingOperationId(null);
        setPendingOperationRetryAfterSeconds(null);
        submitInFlightRef.current = false;
        setSubmitError(
          'The confirmed operation does not match this appointment. The cancellation remains saved for review.',
        );
        return;
      }
      const handled = recordConfirmed();
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      submitInFlightRef.current = false;
      if (!handled) return;
      onCancelled?.();
      close();
    }
    if (operation.status === 'rejected') {
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      submitInFlightRef.current = false;
      const command = currentCommand();
      if (command) {
        rereadAfterRejection({
          ...command,
          operationId: operation.id,
        }).catch(() => undefined);
      }
      setSubmitError(
        appointmentRejectionMessage(
          'rejection' in operation ? operation.rejection : null,
          'cancellation',
          'We could not cancel this session. Please try again.',
        ),
      );
    }
    if (operation.status === 'needs_reconciliation') {
      const command = currentCommand();
      if (command) {
        lifecycle.recordNeedsReconciliation({
          ...command,
          operationId: operation.id,
        });
      }
      setPendingOperationId(null);
      setPendingOperationRetryAfterSeconds(null);
      submitInFlightRef.current = false;
      setOperationReviewRequired(true);
      setSubmitError(
        'The provider outcome requires reconciliation. This cancellation remains saved for Mento operations review.',
      );
    }
  }, [
    close,
    onCancelled,
    operationStatus.currentData,
    pendingOperationId,
    rereadAfterRejection,
    recordConfirmed,
    currentCommand,
    nativeAppointmentId,
  ]);

  const onSubmit = async (retryRestoredSubmitting = false) => {
    if (
      submitInFlightRef.current ||
      pendingOperationId ||
      baselineDriftDetected ||
      invalidRestoredSubmittingCommand ||
      isNativeBaselineError ||
      rejectedCancellationCommandUnchanged ||
      restoredCancellationReasonChanged ||
      restoredCancellationScopeChanged ||
      operationReviewRequired ||
      upgradeRequired
    )
      return;
    // MEN-4241: block a NEW cancellation once the session has started, but never
    // the replay of one that was already sent. The retained branch below resends
    // the preserved command under its original idempotency key, for a request
    // submitted before the start boundary; the server is authoritative through
    // that key and answers with the original outcome. Intercepting it here would
    // strand the member on a permanently unknown result with an enabled
    // "Try cancellation again" button that does nothing, because
    // `cancellationOutcomeUnresolved` keeps `restoredSubmitting` ahead of the
    // past card. PR #1650 review.
    //
    // This is also what keeps `sessionPassed` coherent: every remaining path
    // that reaches the latch has no unresolved outcome (the others all disable
    // the submit button), so the past card is free to render.
    const replayingRetainedCommand = Boolean(
      retryRestoredSubmitting && lifecycle.restoredSubmitting,
    );
    if (
      !replayingRetainedCommand &&
      authoritativeStartsAt &&
      isSessionStarted(authoritativeStartsAt)
    ) {
      setSessionPassed(true);
      return;
    }
    submitInFlightRef.current = true;
    setSubmitError(null);
    setPendingOperationId(null);
    setPendingOperationRetryAfterSeconds(null);
    try {
      if (nativeAppointmentId) {
        let command: RetainedCancellationCommand;
        let bytes: string;
        if (retryRestoredSubmitting && lifecycle.restoredSubmitting) {
          const retainedCommand = retainedCancellationCommand(
            lifecycle.restoredSubmitting.bytes,
            nativeAppointmentId,
          );
          if (
            !retainedCommand ||
            retainedCommand.reason !== (reason.trim() || null) ||
            retainedCommand.scope !== displayedScope
          ) {
            submitInFlightRef.current = false;
            setSubmitError(
              'The changed request cannot replace the saved cancellation. Restore the original details or contact support.',
            );
            return;
          }
          command = retainedCommand;
          bytes = lifecycle.restoredSubmitting.bytes;
        } else {
          const preflight = await lifecycle.read();
          if (preflight.kind !== 'ready') {
            submitInFlightRef.current = false;
            if (preflight.kind === 'upgrade') setUpgradeRequired(true);
            else {
              if (preflight.preimage) setBaselineDriftDetected(true);
              setSubmitError(
                'Appointment details changed. Close and reopen this dialog to review the latest session before trying again.',
              );
            }
            return;
          }
          const appointment = preflight.preimage;
          command = {
            appointmentId: nativeAppointmentId,
            expectedVersion: appointment.version,
            reason: reason.trim() || null,
            scope: displayedScope,
            ...appointmentSeriesCommandFields(
              appointmentCapability,
              appointment,
            ),
          };
          bytes = canonicalAppointmentCommand(command);
        }
        if (lifecycle.submissionBlocked(bytes)) {
          submitInFlightRef.current = false;
          setSubmitError(
            'This rejected cancellation is unchanged. Review the refreshed session and change the request before submitting again.',
          );
          return;
        }
        const submission = lifecycle.submissionFor(
          bytes,
          retryRestoredSubmitting,
        );
        if (submission.kind === 'review') {
          submitInFlightRef.current = false;
          setSubmitError(
            submission.reason === 'changed-command'
              ? 'The changed request cannot replace the saved cancellation. Restore the original details or contact support.'
              : 'The previous cancellation outcome is unknown. Use the explicit unchanged retry or contact support.',
          );
          return;
        }
        const { idempotencyKey } = submission;
        if (!lifecycle.recordSubmitting({ bytes, idempotencyKey })) {
          submitInFlightRef.current = false;
          setSubmitError(
            'We could not safely save this cancellation. Please enable session storage and try again.',
          );
          return;
        }
        const result = useDirectParticipantTransport
          ? await cancelParticipantAppointment({
              appointmentId: command.appointmentId,
              expectedSeriesMutationVersion: null,
              expectedVersion: command.expectedVersion,
              idempotencyKey,
              reason: command.reason,
              replacementAppointmentId: null,
              scope: 'appointment',
              seriesId: null,
            }).unwrap()
          : await cancelAppointment({
              ...command,
              contractVersion: 'appointment-cancel.v1',
              idempotencyKey,
              userId,
            }).unwrap();
        if (result.status === 'pending') {
          setUpgradeRequired(false);
          lifecycle.recordPending({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
            retryAfterSeconds: result.retryAfterSeconds ?? null,
          });
          setPendingOperationId(result.operationId);
          setPendingOperationRetryAfterSeconds(
            result.retryAfterSeconds ?? null,
          );
          submitInFlightRef.current = false;
          return;
        }
        if (result.status === 'rejected') {
          submitInFlightRef.current = false;
          await rereadAfterRejection({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
          });
          setSubmitError(
            appointmentRejectionMessage(
              'rejection' in result ? result.rejection : null,
              'cancellation',
              'We could not cancel this session. Please try again.',
            ),
          );
          return;
        }
        if (result.status === 'needs_reconciliation') {
          lifecycle.recordNeedsReconciliation({
            bytes,
            idempotencyKey,
            operationId: result.operationId,
          });
          setOperationReviewRequired(true);
          setSubmitError(
            'The provider outcome requires reconciliation. This cancellation remains saved for Mento operations review.',
          );
          submitInFlightRef.current = false;
          return;
        }
      } else {
        await cancelSession({
          reason: reason.trim(),
          sessionId: session.id,
        }).unwrap();
      }
      const handled = !nativeAppointmentId || recordConfirmed();
      submitInFlightRef.current = false;
      if (!handled) return;
      onCancelled?.();
      close();
    } catch (error) {
      if (!nativeAppointmentId) {
        try {
          const request = refreshSessionAfterFailure(session.id);
          const authoritativeSession = await request
            .unwrap()
            .finally(() => request.unsubscribe());
          if (authoritativeSession.status === 'cancelled') {
            onCancelled?.();
            close();
            return;
          }
        } catch {
          // Preserve the original cancellation error when reconciliation fails.
        }
      }
      const upgradeGuardSpent = appointmentUpgradeGuardSpent(error);
      const upgradeRetainsOwnership =
        upgradeGuardSpent ||
        (error as { upgrade?: { state?: unknown } }).upgrade?.state ===
          'reload-requested';
      if (upgradeRetainsOwnership) setUpgradeRequired(true);
      if (
        nativeAppointmentId &&
        !upgradeRetainsOwnership &&
        !(useDirectParticipantTransport
          ? shouldReconcileParticipantAppointmentWrite(error)
          : shouldReconcileAppointmentWrite(error))
      ) {
        clearOwnedOperation();
      }
      submitInFlightRef.current = false;
      setSubmitError('We could not cancel this session. Please try again.');
    }
  };

  const policyLink = (
    <a
      className="text-sm font-medium text-sage-500 hover:underline"
      href={SCHEDULING_CANCELLATION_POLICY_URL}
      rel="noreferrer"
      target="_blank"
    >
      Scheduling &amp; Cancellation Policy
    </a>
  );

  let content: ReactNode;
  if (isPast) {
    content = (
      <SessionCardShell className="md:max-w-[482px]">
        <CoachHeader session={session} />
        <Typography
          className="font-medium"
          id="member-cancel-session-title"
          variant="h4"
        >
          This session&apos;s time has passed
        </Typography>
        <DetailRow icon="event">
          {formatCancellationSessionRange(displayedSession)}
        </DetailRow>
        <DetailRow icon="info_outlined">
          Past sessions can&apos;t be rescheduled or cancelled.
        </DetailRow>
        <Button
          className="self-end rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500"
          variant="contained"
          onClick={requestClose}
        >
          Dismiss
        </Button>
      </SessionCardShell>
    );
  } else if (step === 'speedbump') {
    content = (
      <SessionCardShell>
        <CoachHeader session={session} />
        <Typography
          className="font-medium"
          id="member-cancel-session-title"
          variant="h4"
        >
          Before you cancel
        </Typography>
        <div className="flex items-start gap-3 text-cement-400">
          <Icon className="text-xl">info_outlined</Icon>
          <div className="flex flex-col gap-2">
            <Typography className="text-base">
              {isWithin
                ? "This session is too close to reschedule and won't be refunded if cancelled."
                : 'Cancelled sessions are non-refundable. Rescheduling keeps the time on your calendar.'}
            </Typography>
            {policyLink}
          </div>
        </div>
        <DetailRow icon="event">
          {formatCancellationSessionRange(displayedSession)}
        </DetailRow>
        {nativeRouteUnavailable ? (
          <Alert severity="error">
            Appointment cancellation is temporarily unavailable.
          </Alert>
        ) : null}
        <div className="flex justify-end gap-4">
          {isWithin ? (
            <>
              <Button variant="text" onClick={requestClose}>
                Back
              </Button>
              <Button
                className="rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500"
                variant="contained"
                onClick={() => setStep('form')}
              >
                Continue to cancel
              </Button>
            </>
          ) : (
            <>
              <Button variant="text" onClick={() => setStep('form')}>
                Continue to Cancel
              </Button>
              <Button
                className="rounded-xl bg-sage-500 px-5 text-white shadow-none hover:bg-sage-500"
                variant="contained"
                onClick={onRescheduleInstead}
              >
                Reschedule instead
              </Button>
            </>
          )}
        </div>
      </SessionCardShell>
    );
  } else {
    content = (
      <SessionCardShell className="max-md:min-h-[calc(100vh-68px)] max-md:max-w-none max-md:rounded-none max-md:border-0 max-md:p-8 max-md:shadow-none">
        <CoachHeader session={session} />
        <Typography
          className="font-medium"
          id="member-cancel-session-title"
          variant="h4"
        >
          Cancel session
        </Typography>
        <DetailRow icon="event">
          {formatCancellationSessionRange(displayedSession)}
        </DetailRow>
        <div className="flex flex-col gap-3">
          <DetailRow icon="chat_bubble">
            Note to {session.coach.firstName}
          </DetailRow>
          <TextField
            minRows={3}
            placeholder="Why are you cancelling? (Required)"
            value={reason}
            fullWidth
            multiline
            required
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        {cancellationStatusMessage ? (
          <Alert severity="info">{cancellationStatusMessage}</Alert>
        ) : null}
        {nativeRouteUnavailable ? (
          <Alert severity="error">
            Appointment cancellation is temporarily unavailable.
          </Alert>
        ) : null}
        <div
          aria-label="Cancellation actions"
          className="flex justify-end gap-4 max-md:mt-auto max-md:flex-col"
          role="group"
        >
          <Button variant="text" onClick={requestClose}>
            Back
          </Button>
          <Button
            className="max-md:w-full"
            color="error"
            disabled={
              isSubmitting ||
              isNativeSubmitting ||
              participantCancellationMutation.isLoading ||
              submitInFlightRef.current ||
              nativeRouteBlocked ||
              isNativeBaselineError ||
              isNativeBaselineLoading ||
              isNativeBaselinePending ||
              nativeCancellationBlocksSubmit ||
              lifecycle.isReading ||
              baselineDriftDetected ||
              isReconcilingRestored ||
              operationReviewRequired ||
              rejectedCancellationCommandUnchanged ||
              restoredReviewUnavailable ||
              invalidRestoredSubmittingCommand ||
              restoredCancellationReasonChanged ||
              restoredCancellationScopeChanged ||
              upgradeRequired ||
              Boolean(pendingOperationId) ||
              (!reason.trim() && !lifecycle.restoredSubmitting)
            }
            variant="contained"
            onClick={() => onSubmit(hasRecoverableSubmittingCommand)}
          >
            {cancellationButtonLabel}
          </Button>
        </div>
      </SessionCardShell>
    );
  }

  return (
    <MentoCalPageShell
      labelledBy="member-cancel-session-title"
      onClose={requestClose}
    >
      {content}
    </MentoCalPageShell>
  );
};

export type { MemberCancelSessionProps };
